import React, { useState } from 'react';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import { dovsService, dovsSchema, DOVSResultResponse } from '../../services/verificationSystemServices/dovsService';
import { Box, Alert, CircularProgress, Button } from '@mui/material';

const DigitalOnboardingVerificationSystem = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [finalResult, setFinalResult] = useState<DOVSResultResponse | null>(null);

    const handleSubmit = async (data: any) => {
        setIsLoading(true);
        setError(null);
        setActiveRequestId(null);
        setSuccessMessage(null);
        setFinalResult(null);

        try {
            // Initiate Phase 1 - This will send the SMS
            console.log('Dispatching SMS: ', data);
            const initResponse = await dovsService.initiateDOVS(data);

            if (initResponse.success) {
                setSuccessMessage(initResponse.message);
                setActiveRequestId(initResponse.request_id);
            } else {
                setError(initResponse.message || 'Initialization Failed');
            }
        } catch (err: any) {
            console.error('DOVS Initiation Error:', err);
            setError(err.response?.data?.detail || err.message || 'System fault triggering onboarding');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePollResult = async () => {
        if (!activeRequestId) return;
        setIsPolling(true);
        setError(null);

        try {
            const pollResponse = await dovsService.checkDOVSResult(activeRequestId);

            if (pollResponse.status === 'COMPLETED') {
                setSuccessMessage("Facial Verification Processing complete!");
                setFinalResult(pollResponse);
                setActiveRequestId(null); // Clear active tracking to reset
            } else if (pollResponse.status === 'AWAITING_VERIFICATION') {
                setSuccessMessage("SMS is still pending user action. Refresh again shortly...");
            } else {
                setError(pollResponse.message || "Facial match failed.");
            }
        } catch (err: any) {
            console.error('DOVS Polling Error:', err);
            setError(err.response?.data?.detail || err.message || 'System fault polling results');
        } finally {
            setIsPolling(false);
        }
    };

    const resetForm = () => {
        setActiveRequestId(null);
        setError(null);
        setFinalResult(null);
        setSuccessMessage(null);
    };

    const formSchema: FormField[] = [
        {
            name: 'id_number',
            label: 'ID Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: true,
            maxLength: 13,
        },
        {
            name: 'cell_number',
            label: 'Cellphone Number (eg. 082000000)',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: true,
        },
        {
            name: 'your_reference',
            label: 'Your Reference',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'voucher_code',
            label: 'Voucher Code',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
    ];

    return (
        <VerificationPageLayout title="Digital Onboarding Verification">
            {!activeRequestId && !finalResult ? (
                <SchemaForm
                    schema={formSchema}
                    onSubmit={handleSubmit}
                    submitLabel={isLoading ? "Triggering DOVS SMS..." : "Start DOVS Validation"}
                    validationSchema={dovsSchema}
                />
            ) : (
                <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        {successMessage || "SMS Dispatched. Awaiting user to complete facial verification."}
                    </Alert>

                    {activeRequestId && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePollResult}
                                disabled={isPolling}
                            >
                                {isPolling ? <CircularProgress size={24} color="inherit" /> : 'Fetch Processing Profile'}
                            </Button>
                            <Button variant="outlined" color="error" onClick={resetForm}>
                                Cancel Enquiry
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            )}

            {finalResult && finalResult.report_data && (
                <Box sx={{ mt: 2 }}>
                    <VerificationResultDisplay
                        title="Digital Onboarding Verification"
                        success={finalResult.success}
                        message="Digital Liveness Matching Acquired successfully"
                        data={finalResult.report_data}
                    />
                    <Button variant="outlined" sx={{ mt: 3, borderColor: '#2D4677', color: '#2D4677' }} onClick={resetForm}>
                        Start New Onboarding
                    </Button>
                </Box>
            )}
        </VerificationPageLayout>
    );
};

export default DigitalOnboardingVerificationSystem;
